import React, { createContext, useContext, useMemo } from 'react';
import type { Theme } from 'ag-grid-community';
import { eLocalStorageKeys } from 'src/constants/AppConstants';
import { usePersistedState } from 'src/hooks/useLocalStorage';
import { getFinSuiteAgGridTheme, type FinSuiteAgGridThemeMode } from 'src/styles/ag-grid-theme';

// Kept for callers that still need a plain mode string.
// v35 theming uses the `theme` prop on <AgGridReact> — see agGridTheme below.
export const AG_GRID_THEME_MODE = {
  LIGHT: 'light',
  DARK: 'dark'
} as const;

interface ThemeContextType {
  isDarkMode: boolean;
  setIsDarkMode: (value: boolean) => void;
  /** v35 Theming API Theme object — pass to <AgGridReact theme={agGridTheme}> */
  agGridTheme: Theme;
  /** 'light' | 'dark' */
  themeMode: FinSuiteAgGridThemeMode;
}

const ThemeContextInitialData: ThemeContextType = {
  isDarkMode: true,
  setIsDarkMode: () => {},
  agGridTheme: getFinSuiteAgGridTheme('dark'),
  themeMode: 'dark'
};

const ThemeContext = createContext<ThemeContextType>(ThemeContextInitialData);

export const useTheme = () => useContext(ThemeContext);

interface ThemeContextProviderProps {
  children: React.ReactNode;
}

export const ThemeContextProvider = ({ children }: ThemeContextProviderProps) => {
  // Use the same localStorage key as DFPTopNavigation for consistency
  const [isDarkMode, setIsDarkMode] = usePersistedState(eLocalStorageKeys.IsDarkModeEnabled, false);

  const themeMode: FinSuiteAgGridThemeMode = isDarkMode ? 'dark' : 'light';
  const agGridTheme = useMemo(() => getFinSuiteAgGridTheme(themeMode), [themeMode]);

  const contextValue: ThemeContextType = useMemo(
    () => ({ isDarkMode, setIsDarkMode, agGridTheme, themeMode }),
    [isDarkMode, setIsDarkMode, agGridTheme, themeMode]
  );

  return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
};
